import { useState } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem as MuiMenuItem,
  Grid,
} from '@mui/material';
import {
  AddIcon,
  PersonFilledIcon,
  SettingsIcon,
} from 'components/common/Icon/CommonIcon';
import { FormationsGhostButton } from 'components/common/Buttons';
import { Routes } from 'fnRoutes';
import { BusinessPlan } from 'models/business';
import { ENTITY_MAPPING } from 'constants/common';
import { queryClient } from 'states/reactQueryClient';
import { isOwner } from 'components/UserManagement/helpers';
import { showErrorToast } from 'components/toast/showToast';
import smallLogo from 'components/common/Icon/assets/formationsLogo.png';
import { useIndustryOptions, useNavigate } from 'hooks/api';
import { FormationsFormDialog } from 'components/common/FormationsFormDialog';
import { useCreateCompany } from 'hooks/api/useCompanies';
import * as yup from 'yup';
import { useFormationsAuth } from 'auth/FormationsAuthProvider';
import {
  IFormField,
  FormationsFormFields,
} from 'components/common/FormationsForm2';
import { BUSINESS_OWNERSHIP, BusinessOwnershipOptionToLabel } from 'enums';
import {
  CUSTOMER_MULTI_BUSINESS_FLAG,
  CUSTOMER_MULTI_USER_FLAG,
  useFeatureFlag,
} from 'hooks/useFeatureFlag';

export const createBusinessSchema = yup.object().shape({
  industryCode: yup.string().required('Please select an industry.'),
  entityType: yup.string().required('Please select an entity type.'),
  formationsPlan: yup.string().required('Please select a plan.'),
  businessOwnership: yup
    .string()
    .required('Please select a business ownership structure.'),
});

export const CustomerBusinessSelector = () => {
  const navigate = useNavigate();
  const showUserSelector = useFeatureFlag(CUSTOMER_MULTI_USER_FLAG);
  const showBusinessSelector = useFeatureFlag(CUSTOMER_MULTI_BUSINESS_FLAG);
  const [openSelectBusiness, setOpenSelectBusiness] = useState(false);
  const { data: industryOptionsData } = useIndustryOptions();
  const [showAddBusinessModal, setShowAddBusinessModal] = useState(false);
  const {
    userBusinesses,
    userAccounts,
    currentCompanyId,
    currentUserId,
    handleSwitchCompany,
  } = useFormationsAuth();
  const { createCompanyAsync, isLoading: isCreateCompanyLoading } =
    useCreateCompany();

  const currentBusiness = userBusinesses.find((b) => b.id === currentCompanyId);

  const formFields: IFormField[] = [
    {
      type: FormationsFormFields.AutoComplete,
      name: 'industryCode',
      label: 'Business Industry',
      options: {
        placeholder: '',
        options: industryOptionsData?.map(({ title, code }) => ({
          name: title,
          value: code,
        })),
      },
    },
    {
      type: FormationsFormFields.Select,
      name: 'entityType',
      label: 'Entity Type',
      options: {
        placeholder: 'Select an option',
        options: [
          {
            value: ENTITY_MAPPING.sole_prop,
            label: 'Sole Proprietorship',
          },
          {
            value: ENTITY_MAPPING.llc,
            label: 'LLC',
          },
          {
            value: ENTITY_MAPPING.s_corp,
            label: 'S Corp',
          },
          {
            value: ENTITY_MAPPING.c_corp,
            label: 'C Corp',
          },
        ],
      },
    },
    {
      type: FormationsFormFields.Select,
      name: 'formationsPlan',
      label: 'Plan',
      options: {
        placeholder: 'Select a plan',
        options: [
          {
            value: BusinessPlan.FullSuite,
            label: 'Full Suite',
          },
          {
            value: BusinessPlan.TaxOnly,
            label: 'Tax Only',
          },
          {
            value: BusinessPlan.BIB,
            label: 'BIB',
          },
        ],
      },
    },
    {
      type: FormationsFormFields.Select,
      name: 'businessOwnership',
      label: 'Business Ownership',
      options: {
        placeholder: 'Select a business ownership structure',
        options: [
          {
            value: BUSINESS_OWNERSHIP.INDIVIDUAL,
            label:
              BusinessOwnershipOptionToLabel[BUSINESS_OWNERSHIP.INDIVIDUAL],
          },
          {
            value: BUSINESS_OWNERSHIP.PARTNERSHIP,
            label:
              BusinessOwnershipOptionToLabel[BUSINESS_OWNERSHIP.PARTNERSHIP],
          },
        ],
      },
    },
  ];

  const handleOpenMenu = () => {
    setOpenSelectBusiness(true);
  };

  const handleCloseMenu = () => {
    setOpenSelectBusiness(false);
  };

  const displayAddBusinessModal = () => {
    setShowAddBusinessModal(true);
    handleCloseMenu();
  };

  const handleAddBusiness = async (data: any) => {
    const payload = {
      formationsPlan: data.formationsPlan,
      entityType: data.entityType,
      originalEntityType: data.entityType,
      industryCode: data.industryCode,
      businessOwnership: data.businessOwnership,
    };
    try {
      const res = (await createCompanyAsync(payload)) as {
        id: string;
        accountId: string;
      };
      await queryClient.invalidateQueries({
        queryKey: ['currentCompanyList'],
      });
      handleCloseMenu();
      if (res.id && res.accountId) {
        handleSwitchCompany(res.id, res.accountId);
      }
    } catch (error) {
      showErrorToast('Failed to create business');
      console.error(error);
    }
  };

  const handleManageUsers = () => {
    handleCloseMenu();
    navigate(Routes.USER_MANAGEMENT);
  };

  const handleBusinessProfile = () => {
    handleCloseMenu();
    navigate(Routes.MY_COMPANY);
  };

  return (
    <Box mb={1} display="flex" alignItems="center" justifyContent="center">
      <Select
        labelId="filter-business"
        id="filterBusiness"
        name="filterBusiness"
        value={currentCompanyId}
        variant="outlined"
        sx={{
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          width: '200px',
          backgroundColor: 'white',
        }}
        open={openSelectBusiness}
        onOpen={handleOpenMenu}
        onClose={handleCloseMenu}
        onChange={(e) => {
          if (e.target.value !== '') {
            handleSwitchCompany(e.target.value);
          }
          handleCloseMenu();
        }}
        renderValue={(value) => {
          const business = userBusinesses.find((b) => b.id === value);
          if (!business) {
            return null;
          }
          return (
            <Grid
              container
              display="flex"
              alignItems="center"
              flexDirection="row"
              justifyContent="center"
            >
              <img
                src={smallLogo}
                alt={business.name}
                width="24px"
                height="24px"
                style={{ marginRight: '8px' }}
              />
              <Typography
                variant="body1S"
                component="p"
                width="120px"
                maxWidth="120px"
                overflow="hidden"
                textOverflow="ellipsis"
              >
                {business.name ?? business.id}
              </Typography>
            </Grid>
          );
        }}
      >
        {currentBusiness && (
          <Grid
            container
            width="200px"
            p={2}
            justifyContent="center"
            flexDirection="column"
          >
            <Grid
              container
              display="flex"
              alignItems="flex-start"
              flexDirection="row"
              width="168px"
            >
              <img
                src={smallLogo}
                alt={currentBusiness?.name}
                width="24px"
                height="24px"
                style={{ marginRight: '8px' }}
              />
              <Grid item width="136px" flexDirection="column">
                <Typography
                  variant="body2B"
                  component="p"
                  width="136px"
                  maxWidth="136px"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  marginBottom="4px"
                >
                  {currentBusiness?.name ?? currentBusiness?.id}
                </Typography>
                <Typography
                  variant="body3S"
                  color="text.secondary"
                  component="p"
                  width="136px"
                  maxWidth="136px"
                  overflow="hidden"
                  textOverflow="ellipsis"
                >
                  {isOwner(currentBusiness, userAccounts, currentUserId)
                    ? 'Owner'
                    : 'Member'}
                </Typography>
              </Grid>
            </Grid>
            <Grid item width="168px" mt={1}>
              <FormationsGhostButton
                startIcon={<SettingsIcon size={20} />}
                sx={{ width: '168px', justifyContent: 'flex-start' }}
                onClick={handleBusinessProfile}
              >
                Business Profile
              </FormationsGhostButton>
              {isOwner(currentBusiness, userAccounts, currentUserId) &&
                showUserSelector && (
                  <FormationsGhostButton
                    startIcon={<PersonFilledIcon size={20} />}
                    sx={{ width: '168px', justifyContent: 'flex-start' }}
                    onClick={handleManageUsers}
                  >
                    Manage Users
                  </FormationsGhostButton>
                )}
            </Grid>
          </Grid>
        )}

        {userBusinesses.map((business) => (
          <MuiMenuItem
            key={business.id}
            value={business.id}
            sx={{
              width: '200px',
            }}
          >
            <Grid
              container
              display="flex"
              alignItems="flex-start"
              flexDirection="row"
              width="168px"
            >
              <img
                src={smallLogo}
                alt={business.name}
                width="24px"
                height="24px"
                style={{ marginRight: '8px' }}
              />
              <Grid item width="136px" flexDirection="column">
                <Typography
                  variant="body2B"
                  component="p"
                  width="136px"
                  maxWidth="136px"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  marginBottom="4px"
                >
                  {business.name ?? business.id}
                </Typography>
                <Typography
                  variant="body3S"
                  color="text.secondary"
                  component="p"
                  width="136px"
                  maxWidth="136px"
                  overflow="hidden"
                  textOverflow="ellipsis"
                >
                  {isOwner(business, userAccounts, currentUserId)
                    ? 'Owner'
                    : 'Member'}
                </Typography>
              </Grid>
            </Grid>
          </MuiMenuItem>
        ))}
        {showBusinessSelector && (
          <Grid container width="200px" p={2} justifyContent="center">
            <FormationsGhostButton
              startIcon={<AddIcon size={24} />}
              sx={{ width: '168px' }}
              onClick={displayAddBusinessModal}
            >
              New Business
            </FormationsGhostButton>
          </Grid>
        )}
      </Select>
      {showAddBusinessModal && (
        <FormationsFormDialog
          title="Add Business"
          isOpen={showAddBusinessModal}
          onClose={() => setShowAddBusinessModal(false)}
          onSubmit={handleAddBusiness}
          fields={formFields}
          data-testid="add-business-dialog"
          useFormOptions={{
            mode: 'onSubmit',
            reValidateMode: 'onChange',
          }}
          validationSchema={createBusinessSchema}
          loading={isCreateCompanyLoading}
        />
      )}
    </Box>
  );
};
